import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Close } from '@mui/icons-material';
import { colors, borderRadius, shadows, spacing, typography } from '../styles/theme';

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  z-index: 200;
`;

const DrawerContainer = styled(motion.div)<{ position: 'left' | 'right' | 'bottom' }>`
  position: fixed;
  z-index: 201;
  background: ${colors.backgroundSecondary};
  box-shadow: ${shadows.xl};
  border-radius: ${props =>
    props.position === 'bottom'
      ? `${borderRadius.xl} ${borderRadius.xl} 0 0`
      : props.position === 'left'
        ? `0 ${borderRadius.xl} ${borderRadius.xl} 0`
        : `${borderRadius.xl} 0 0 ${borderRadius.xl}`
  };

  ${props => {
    switch (props.position) {
      case 'left':
        return `
          top: 0;
          left: 0;
          bottom: 0;
          width: 320px;
          max-width: 85vw;
        `;
      case 'right':
        return `
          top: 0;
          right: 0;
          bottom: 0;
          width: 320px;
          max-width: 85vw;
        `;
      case 'bottom':
        return `
          left: 0;
          right: 0;
          bottom: 0;
          max-height: 80vh;
          border-top: 1px solid ${colors.borderLight};
        `;
    }
  }}
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing.lg};
  border-bottom: 1px solid ${colors.borderLight};
`;

const DrawerTitle = styled.h2`
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.xl};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.textPrimary};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${colors.textTertiary};
  font-size: ${typography.fontSize.xl};
  cursor: pointer;
  padding: ${spacing.sm};
  border-radius: ${borderRadius.md};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.backgroundTertiary};
    color: ${colors.textPrimary};
  }

  &:active {
    transform: scale(0.9);
  }
`;

const DrawerContent = styled.div`
  padding: ${spacing.lg};
  overflow-y: auto;
  max-height: calc(100vh - 80px);
`;

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  position?: 'left' | 'right' | 'bottom';
  children: React.ReactNode;
}

const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  title,
  position = 'right',
  children
}) => {
  const getInitialPosition = () => {
    switch (position) {
      case 'left': return { x: '-100%' };
      case 'right': return { x: '100%' };
      case 'bottom': return { y: '100%' };
    }
  };

  const getAnimatePosition = () => {
    switch (position) {
      case 'left': return { x: 0 };
      case 'right': return { x: 0 };
      case 'bottom': return { y: 0 };
    }
  };

  const getExitPosition = () => {
    switch (position) {
      case 'left': return { x: '-100%' };
      case 'right': return { x: '100%' };
      case 'bottom': return { y: '100%' };
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />
          <DrawerContainer
            position={position}
            initial={getInitialPosition()}
            animate={getAnimatePosition()}
            exit={getExitPosition()}
            transition={{
              type: 'spring',
              damping: 30,
              stiffness: 300,
              duration: 0.3
            }}
          >
            <DrawerHeader>
              <DrawerTitle>{title}</DrawerTitle>
              <CloseButton onClick={onClose}>
                <Close sx={{ fontSize: 20 }} />
              </CloseButton>
            </DrawerHeader>
            <DrawerContent>
              {children}
            </DrawerContent>
          </DrawerContainer>
        </>
      )}
    </AnimatePresence>
  );
};

export default Drawer;
